// pages/LogViewer.js - Halaman Activity Log untuk Admin
import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
import useIsMobile from '../hooks/useIsMobile';
import CardList from '../components/mobile/CardList';
import DetailSheet from '../components/mobile/DetailSheet';
import {
  Activity, Search, RefreshCw, Download, Filter,
  LogIn, LogOut, LayoutDashboard, Bot, ShieldAlert,
  Monitor, ChevronLeft, ChevronRight, TrendingUp,
  Users, AlertTriangle, CheckCircle2, Clock,
} from 'lucide-react';

// ── Konstanta ─────────────────────────────────────────────────────────────────
// labelKey → di-resolve via t() saat render agar ikut bahasa aktif.
const ACTION_META = {
  LOGIN_SUCCESS:  { labelKey: 'log.actLoginOk',     icon: LogIn,          color: 'text-green-600',  bg: 'bg-green-50',  dot: 'bg-green-500'  },
  LOGIN_FAILED:   { labelKey: 'log.actLoginFail',   icon: ShieldAlert,    color: 'text-red-600',    bg: 'bg-red-50',    dot: 'bg-red-500'    },
  LOGOUT:         { labelKey: 'log.actLogout',      icon: LogOut,         color: 'text-slate-600',  bg: 'bg-slate-50',  dot: 'bg-slate-400'  },
  VIEW_DASHBOARD: { labelKey: 'log.actViewDash',    icon: LayoutDashboard,color: 'text-blue-600',   bg: 'bg-blue-50',   dot: 'bg-blue-500'   },
  OPEN_AI:        { labelKey: 'log.actOpenAi',      icon: Bot,            color: 'text-purple-600', bg: 'bg-purple-50', dot: 'bg-purple-500' },
  SAVE_AI_CONFIG: { labelKey: 'log.actSaveAiConfig',icon: Bot,            color: 'text-indigo-600', bg: 'bg-indigo-50', dot: 'bg-indigo-500' },
};

const ROLE_STYLES = {
  admin: 'bg-blue-100 text-blue-700',
  SAP:   'bg-emerald-100 text-emerald-700',
  AWS:   'bg-amber-100 text-amber-700',
};

function getActionMeta(action) {
  return ACTION_META[action] || {
    labelKey: null, icon: Activity,
    color: 'text-slate-600', bg: 'bg-slate-50', dot: 'bg-slate-400',
  };
}

// Tone badge kartu mobile berdasar makna action (CardList: emerald/rose/amber/blue/slate).
function logTone(action = '') {
  const a = action.toUpperCase();
  if (/FAIL|DENIED|ERROR|DELETE|LOCK(?!.*UN)/.test(a)) return 'rose';
  if (/LOGIN|CREATE|ADD|UNLOCK|SUCCESS/.test(a))       return 'emerald';
  if (/UPDATE|EDIT|CHANGE|RESET/.test(a))              return 'amber';
  if (/VIEW|OPEN|EXPORT/.test(a))                       return 'blue';
  return 'slate';
}

// Label aksi siap-tampil: pakai terjemahan bila ada, fallback ke kode action mentah.
function actionLabel(meta, action, t) {
  return meta.labelKey ? t(meta.labelKey) : action;
}

function formatDateTime(str, lang) {
  if (!str) return '—';
  try {
    return new Date(str.replace(' ', 'T') + 'Z').toLocaleString(lang === 'en' ? 'en-US' : 'id-ID', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  } catch { return str; }
}

function formatRelative(str, t) {
  if (!str) return '—';
  try {
    const diff = Date.now() - new Date(str.replace(' ', 'T') + 'Z').getTime();
    const s = Math.floor(diff / 1000);
    if (s < 60)   return t('log.relSec',  { n: s });
    if (s < 3600) return t('log.relMin',  { n: Math.floor(s/60) });
    if (s < 86400) return t('log.relHour', { n: Math.floor(s/3600) });
    return t('log.relDay', { n: Math.floor(s/86400) });
  } catch { return str; }
}

// ── Stat Card ─────────────────────────────────────────────────────────────────
function StatCard({ icon: Icon, label, value, color, bg }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-4 flex items-center gap-4 shadow-sm">
      <div className={`w-11 h-11 rounded-xl ${bg} flex items-center justify-center flex-shrink-0`}>
        <Icon className={color} style={{ width: 20, height: 20 }} />
      </div>
      <div>
        <div className="text-2xl font-bold text-slate-800">{value ?? '—'}</div>
        <div className="text-xs text-slate-400 font-medium">{label}</div>
      </div>
    </div>
  );
}

// ── Log Row ───────────────────────────────────────────────────────────────────
function LogRow({ log, index }) {
  const { t, lang } = useLang();
  const meta    = getActionMeta(log.action);
  const Icon    = meta.icon;
  const [hover, setHover] = useState(false);

  return (
    <tr
      className={`border-b border-slate-50 transition-colors ${hover ? 'bg-brand-50/40' : index % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {/* ID */}
      <td className="px-4 py-3 text-xs text-slate-400 font-mono w-12">{log.id}</td>

      {/* Waktu */}
      <td className="px-4 py-3 whitespace-nowrap">
        <div className="text-xs font-medium text-slate-700">{formatDateTime(log.created_at, lang)}</div>
        <div className="text-xs text-slate-400 mt-0.5">{formatRelative(log.created_at, t)}</div>
      </td>

      {/* User */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-brand-600 flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
            {(log.username || '?')[0].toUpperCase()}
          </div>
          <div>
            <div className="text-sm font-semibold text-slate-800">{log.username}</div>
            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold ${ROLE_STYLES[log.role] || 'bg-slate-100 text-slate-600'}`}>
              {log.role}
            </span>
          </div>
        </div>
      </td>

      {/* Action */}
      <td className="px-4 py-3">
        <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold ${meta.bg} ${meta.color}`}>
          <Icon style={{ width: 12, height: 12 }} />
          {actionLabel(meta, log.action, t)}
        </div>
      </td>

      {/* Detail */}
      <td className="px-4 py-3 max-w-xs">
        <div className="text-xs text-slate-500 truncate" title={log.detail || ''}>
          {log.detail || <span className="text-slate-300">—</span>}
        </div>
      </td>

      {/* IP Address */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-1.5">
          <Monitor style={{ width: 12, height: 12 }} className="text-slate-300 flex-shrink-0" />
          <span className="text-xs font-mono text-slate-600">
            {log.ip_address || <span className="text-slate-300">—</span>}
          </span>
        </div>
      </td>

      {/* User Agent (short) */}
      <td className="px-4 py-3 max-w-[140px]">
        <div className="text-xs text-slate-400 truncate" title={log.user_agent || ''}>
          {log.user_agent
            ? log.user_agent.replace(/.*?(Chrome|Firefox|Safari|Edge|Opera)\/[\d.]+.*/, '$1')
            : <span className="text-slate-300">—</span>
          }
        </div>
      </td>
    </tr>
  );
}

// ── Main LogViewer ────────────────────────────────────────────────────────────
export default function LogViewer() {
  const { t, lang } = useLang();
  const [logs, setLogs]         = useState([]);
  const [summary, setSummary]   = useState(null);
  const [total, setTotal]       = useState(0);
  const [loading, setLoading]   = useState(false);
  const [page, setPage]         = useState(1);
  const [search, setSearch]     = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [filterUser, setFilterUser]     = useState('');
  const [autoRefresh, setAutoRefresh]   = useState(false);
  const isMobile = useIsMobile();
  const [detailLog, setDetailLog] = useState(null);

  const LIMIT = 50;

  const fetchLogs = useCallback(async (pg = page) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit:  LIMIT,
        offset: (pg - 1) * LIMIT,
        ...(filterAction && { action: filterAction }),
        ...(filterUser   && { username: filterUser }),
      });
      const res  = await api.get(`/api/logs?${params}`);
      let data   = res.data.logs || [];

      // Client-side search filter (username/detail/ip)
      if (search.trim()) {
        const q = search.toLowerCase();
        data = data.filter(l =>
          l.username?.toLowerCase().includes(q) ||
          l.detail?.toLowerCase().includes(q)   ||
          l.ip_address?.includes(q)             ||
          l.action?.toLowerCase().includes(q)
        );
      }

      setLogs(data);
      setTotal(res.data.total || 0);
    } catch (e) {
      console.error('Gagal load logs:', e);
    } finally {
      setLoading(false);
    }
  }, [page, filterAction, filterUser, search]);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await api.get('/api/logs/summary');
      setSummary(res.data);
    } catch {}
  }, []);

  useEffect(() => { fetchLogs(page); }, [page, filterAction, filterUser]);
  useEffect(() => { fetchSummary(); }, []);

  // Auto refresh setiap 30 detik
  useEffect(() => {
    if (!autoRefresh) return;
    const timer = setInterval(() => { fetchLogs(page); fetchSummary(); }, 30000);
    return () => clearInterval(timer);
  }, [autoRefresh, fetchLogs, fetchSummary, page]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchLogs(1);
    setPage(1);
  };

  const handleRefresh = () => { fetchLogs(page); fetchSummary(); };

  const totalPages = Math.ceil(total / LIMIT);

  // Export ke CSV
  const exportCSV = () => {
    const headers = [
      t('log.colId'), t('log.colTime'), t('log.colUsername'), t('log.colRole'),
      t('log.colAction'), t('log.colDetail'), t('log.colIp'), t('log.colBrowser'),
    ];
    const rows = logs.map(l => [
      l.id, l.created_at, l.username, l.role, l.action,
      `"${(l.detail||'').replace(/"/g,'""')}"`,
      l.ip_address||'',
      (l.user_agent||'').replace(/.*?(Chrome|Firefox|Safari|Edge)\/[\d.]+.*/, '$1'),
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `activity_log_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 content-animate">

      {/* ── Header ── */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-display font-bold text-slate-800 text-xl">{t('log.title')}</h2>
          <p className="text-sm text-slate-400 mt-0.5">
            {t('log.subtitle', { n: total.toLocaleString() })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Auto Refresh Toggle */}
          <button
            onClick={() => setAutoRefresh(p => !p)}
            className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold border transition-all
              ${autoRefresh ? 'bg-green-50 text-green-700 border-green-200' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'}`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${autoRefresh ? 'bg-green-500 animate-pulse' : 'bg-slate-300'}`} />
            {t('log.autoRefresh')} {autoRefresh ? t('log.on') : t('log.off')}
          </button>
          <button onClick={handleRefresh} disabled={loading}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold bg-white border border-slate-200 hover:border-slate-300 text-slate-600 transition-all">
            <RefreshCw style={{ width: 13, height: 13 }} className={loading ? 'animate-spin' : ''} />
            {t('common.refresh')}
          </button>
          <button onClick={exportCSV}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold bg-brand-600 hover:bg-brand-700 text-white transition-all">
            <Download style={{ width: 13, height: 13 }} />
            {t('log.exportCsv')}
          </button>
        </div>
      </div>

      {/* ── Summary Stats ── */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard icon={Activity}      label={t('log.statToday')}       value={summary.today}        color="text-brand-600"   bg="bg-brand-50"   />
          <StatCard icon={CheckCircle2}  label={t('log.statLoginOk')}     value={summary.totalLogins}  color="text-green-600" bg="bg-green-50"  />
          <StatCard icon={AlertTriangle} label={t('log.statLoginFail')}   value={summary.failedLogins} color="text-red-600"   bg="bg-red-50"    />
          <StatCard icon={Users}         label={t('log.statActiveUsers')} value={summary.byUser?.length} color="text-purple-600" bg="bg-purple-50" />
        </div>
      )}

      {/* ── Top Users & Actions ── */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {/* By User */}
          <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp style={{ width: 14, height: 14 }} className="text-brand-500" />
              <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">{t('log.topUsers')}</span>
            </div>
            <div className="space-y-2">
              {(summary.byUser || []).slice(0, 5).map((u, i) => {
                const pct = Math.round((u.count / (summary.byUser[0]?.count || 1)) * 100);
                return (
                  <div key={u.username} className="flex items-center gap-2">
                    <span className="text-xs text-slate-400 w-4">{i+1}</span>
                    <div className="w-6 h-6 rounded-md bg-brand-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                      {u.username[0].toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-0.5">
                        <span className="text-xs font-semibold text-slate-700 truncate">{u.username}</span>
                        <span className="text-xs text-slate-400 ml-2">{u.count}</span>
                      </div>
                      <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-brand-500 rounded-full" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                    <span className={`text-xs px-1.5 py-0.5 rounded font-semibold ${ROLE_STYLES[u.role] || 'bg-slate-100 text-slate-500'}`}>
                      {u.role}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* By Action */}
          <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <Activity style={{ width: 14, height: 14 }} className="text-purple-500" />
              <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">{t('log.actionDist')}</span>
            </div>
            <div className="space-y-2">
              {(summary.byAction || []).map(a => {
                const meta = getActionMeta(a.action);
                const Icon = meta.icon;
                const pct  = Math.round((a.count / (summary.byAction[0]?.count || 1)) * 100);
                return (
                  <div key={a.action} className="flex items-center gap-2">
                    <div className={`w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 ${meta.bg}`}>
                      <Icon className={meta.color} style={{ width: 11, height: 11 }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-0.5">
                        <span className="text-xs font-medium text-slate-700 truncate">{actionLabel(meta, a.action, t)}</span>
                        <span className="text-xs text-slate-400 ml-2">{a.count}</span>
                      </div>
                      <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${meta.dot}`} style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ── Filters ── */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm mb-4 p-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex items-center gap-2 text-xs font-semibold text-slate-500">
            <Filter style={{ width: 13, height: 13 }} />
            {t('log.filter')}
          </div>

          {/* Search */}
          <form onSubmit={handleSearch} className="flex items-center gap-2">
            <div className="relative">
              <Search style={{ width: 13, height: 13 }} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={search} onChange={e => setSearch(e.target.value)}
                placeholder={t('log.searchPlaceholder')}
                className="pl-8 pr-3 py-1.5 text-xs border border-slate-200 rounded-lg outline-none focus:border-brand-400 w-52"
              />
            </div>
            <button type="submit" className="px-3 py-1.5 bg-brand-600 text-white text-xs font-semibold rounded-lg hover:bg-brand-700 transition-colors">
              {t('log.searchBtn')}
            </button>
          </form>

          {/* Action Filter */}
          <select value={filterAction} onChange={e => { setFilterAction(e.target.value); setPage(1); }}
            className="px-3 py-1.5 text-xs border border-slate-200 rounded-lg outline-none focus:border-brand-400 bg-white text-slate-700">
            <option value="">{t('log.allActions')}</option>
            {Object.entries(ACTION_META).map(([k, v]) => (
              <option key={k} value={k}>{actionLabel(v, k, t)}</option>
            ))}
          </select>

          {/* User Filter */}
          <select value={filterUser} onChange={e => { setFilterUser(e.target.value); setPage(1); }}
            className="px-3 py-1.5 text-xs border border-slate-200 rounded-lg outline-none focus:border-brand-400 bg-white text-slate-700">
            <option value="">{t('log.allUsers')}</option>
            {(summary?.byUser || []).map(u => (
              <option key={u.username} value={u.username}>{u.username}</option>
            ))}
          </select>

          {/* Clear */}
          {(filterAction || filterUser || search) && (
            <button onClick={() => { setFilterAction(''); setFilterUser(''); setSearch(''); setPage(1); setTimeout(() => fetchLogs(1), 0); }}
              className="px-3 py-1.5 text-xs text-red-500 hover:text-red-700 border border-red-200 hover:border-red-300 rounded-lg transition-colors">
              {t('log.resetFilter')}
            </button>
          )}

          <div className="ml-auto text-xs text-slate-400">
            {t('log.showing', { n: logs.length, total: total.toLocaleString() })}
          </div>
        </div>
      </div>

      {/* ── Table ── */}
      {isMobile ? (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <CardList
            items={logs}
            keyOf={(l) => String(l.id)}
            title={(l) => l.username || '—'}
            badge={(l) => ({ label: l.action, tone: logTone(l.action) })}
            subtitle={(l) => [formatDateTime(l.created_at, lang), l.detail].filter(Boolean).join(' · ')}
            onOpen={(l) => setDetailLog(l)}
            emptyText={loading ? t('log.loading') : t('log.empty')}
          />
        </div>
      ) : (
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100">
                {[
                  ['id', t('log.colId')], ['time', t('log.colTime')], ['user', t('log.colUser')],
                  ['action', t('log.colAction')], ['detail', t('log.colDetail')],
                  ['ip', t('log.colIp')], ['browser', t('log.colBrowser')],
                ].map(([k, h]) => (
                  <th key={k} className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center">
                    <div className="flex items-center justify-center gap-2 text-slate-400">
                      <RefreshCw style={{ width: 16, height: 16 }} className="animate-spin" />
                      <span className="text-sm">{t('log.loading')}</span>
                    </div>
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center gap-2 text-slate-300">
                      <Activity style={{ width: 32, height: 32 }} />
                      <span className="text-sm font-medium">{t('log.empty')}</span>
                    </div>
                  </td>
                </tr>
              ) : (
                logs.map((log, i) => <LogRow key={log.id} log={log} index={i} />)
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between bg-slate-50/50">
            <div className="text-xs text-slate-400">
              {t('log.pageInfo', { page, totalPages })}
              {' '}· {t('log.rangeInfo', { from: ((page-1)*LIMIT+1), to: Math.min(page*LIMIT, total), total: total.toLocaleString() })}
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => setPage(1)} disabled={page === 1}
                className="px-2 py-1 text-xs border border-slate-200 rounded-lg disabled:opacity-40 hover:bg-slate-100 transition-colors">«</button>
              <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page === 1}
                className="px-2 py-1 text-xs border border-slate-200 rounded-lg disabled:opacity-40 hover:bg-slate-100 transition-colors flex items-center gap-1">
                <ChevronLeft style={{ width: 12, height: 12 }} /> {t('log.prev')}
              </button>
              {/* Page numbers */}
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const p = Math.max(1, Math.min(totalPages - 4, page - 2)) + i;
                return (
                  <button key={p} onClick={() => setPage(p)}
                    className={`px-2.5 py-1 text-xs border rounded-lg transition-colors
                      ${p === page ? 'bg-brand-600 text-white border-brand-600' : 'border-slate-200 hover:bg-slate-100'}`}>
                    {p}
                  </button>
                );
              })}
              <button onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page === totalPages}
                className="px-2 py-1 text-xs border border-slate-200 rounded-lg disabled:opacity-40 hover:bg-slate-100 transition-colors flex items-center gap-1">
                {t('log.next')} <ChevronRight style={{ width: 12, height: 12 }} />
              </button>
              <button onClick={() => setPage(totalPages)} disabled={page === totalPages}
                className="px-2 py-1 text-xs border border-slate-200 rounded-lg disabled:opacity-40 hover:bg-slate-100 transition-colors">»</button>
            </div>
          </div>
        )}
      </div>
      )}

      {/* Mobile: pager sederhana + detail sheet */}
      {isMobile && totalPages > 1 && (
        <div className="flex items-center justify-between mt-3 px-1">
          <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page === 1}
            className="px-3 py-1.5 text-xs border border-slate-200 rounded-lg disabled:opacity-40 active:bg-slate-100">‹ {t('log.prev')}</button>
          <span className="text-xs text-slate-400">{t('log.pageInfo', { page, totalPages })}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page === totalPages}
            className="px-3 py-1.5 text-xs border border-slate-200 rounded-lg disabled:opacity-40 active:bg-slate-100">{t('log.next')} ›</button>
        </div>
      )}
      {isMobile && detailLog && (
        <DetailSheet
          open
          title={detailLog.username || '—'}
          badge={<span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{detailLog.action}</span>}
          fields={[
            { label: t('log.colId'), value: detailLog.id },
            { label: t('log.colTime'), value: formatDateTime(detailLog.created_at, lang) },
            { label: t('log.colUser'), value: `${detailLog.username || '—'} (${detailLog.role || '—'})` },
            { label: t('log.colAction'), value: detailLog.action },
            { label: t('log.colDetail'), value: detailLog.detail },
            { label: t('log.colIp'), value: detailLog.ip_address },
            { label: t('log.colBrowser'), value: detailLog.user_agent },
          ]}
          onClose={() => setDetailLog(null)}
        />
      )}
    </div>
  );
}
